// /admin/cfdi — every paid invoice the platform still owes a CFDI.
//
// The clinic sees its own fiscal state on its own billing page. What nobody had
// was the view across all of them, which is where "a client paid three weeks
// ago and has no factura" actually becomes visible. `idx_invoices_cfdi_pending`
// was created for this list and had no reader until now.
//
// THE COLUMN THAT MATTERS IS "Bloqueado por". Half the queue is waiting on a
// clinic to type in an RFC and half is waiting on us, and those are completely
// different jobs: one is a phone call, the other is an incident. A single
// undifferentiated "pending" list gets triaged as neither.
//
// Retry does not stamp. It resets the counter and the hourly sweep does the
// work, through the same claim and folio reconciliation as every other
// issuance — because a second path to the one operation that must never happen
// twice is not worth the seconds it would save.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { getCfdiQueue, retryCfdi, type CfdiQueueItem } from './cfdi-queue-data.js'
import {
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyStateCard,
  PageHeader,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

/** Mirrors the union the design system's Chip accepts. */
type ChipTone = 'ok' | 'warn' | 'sens' | 'info' | 'draft'

/** Operator vocabulary, not the client's: this audience wants the real state. */
const STATUS: Record<string, { label: string; tone: ChipTone }> = {
  pending: { label: 'En cola', tone: 'draft' },
  stamping: { label: 'Timbrando', tone: 'warn' },
  pending_fiscal_data: { label: 'Faltan datos fiscales', tone: 'warn' },
  failed: { label: 'Falló', tone: 'sens' },
  issued_docs_missing: { label: 'Timbrada sin archivos', tone: 'sens' },
}

function formatMxn(centavos: number, currency: string): string {
  return (centavos / 100).toLocaleString('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  })
}

/** How long this client has been waiting. The number that makes it urgent. */
function waitingLabel(paidAt: string | null): string {
  if (paidAt === null) return 'Sin fecha de pago'
  const days = Math.floor((Date.now() - new Date(paidAt).getTime()) / 86_400_000)
  if (days <= 0) return 'Pagada hoy'
  if (days === 1) return 'Pagada ayer'
  return `Pagada hace ${String(days)} días`
}

function DocIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-5"
    >
      <path d="M14 3v5h5" />
      <path d="M19 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h9l6 6v11a1 1 0 0 1-1 1Z" />
    </svg>
  )
}

export function CfdiQueuePage(): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'cfdi-queue'],
    queryFn: getCfdiQueue,
  })

  const retry = useMutation({
    mutationFn: (invoiceId: string) => retryCfdi(invoiceId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'cfdi-queue'] })
      // Deliberately not "Timbrada": nothing has been stamped yet. Saying so
      // would be a painted door on the one screen where the difference between
      // "queued" and "issued" is the whole point.
      toast.ok('Encolada para el próximo intento')
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'No pudimos reintentar el timbrado.'))
    },
  })

  // DataTable keys rows by `id`; ours are keyed by the invoice. Aliased here
  // rather than renaming the API field, which would make `invoiceId` ambiguous
  // in the retry call right below it.
  const items = (data?.items ?? []).map((i) => ({ ...i, id: i.invoiceId }))
  const ours = items.filter((i) => i.blockedOn === 'nosotros').length
  const theirs = items.length - ours

  const columns: DataTableColumn<CfdiQueueItem & { id: string }>[] = [
    {
      key: 'tenant',
      label: 'Cliente',
      render: (i) => (
        <span className="block min-w-0">
          <Link
            to={`/admin/tenants/${i.tenantId}`}
            className="block truncate font-semibold text-text hover:text-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
          >
            {i.tenantName}
          </Link>
          <span className="mt-px block text-[11px] text-text-3">
            {i.folio ?? 'Sin folio'} · {waitingLabel(i.paidAt)}
          </span>
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Importe',
      render: (i) => (
        <span className="font-semibold text-text">{formatMxn(i.amountTotalMxn, i.currency)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (i) => {
        const s = STATUS[i.status] ?? { label: i.status, tone: 'draft' as ChipTone }
        return (
          <span className="block">
            <Chip tone={s.tone} dot>
              {s.label}
            </Chip>
            {i.reason !== null && (
              <span className="mt-1 block max-w-[38ch] text-[10.5px] leading-snug text-text-3">
                {i.reason}
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'blocked',
      label: 'Bloqueado por',
      render: (i) => (
        <span className="block">
          <Chip tone={i.blockedOn === 'nosotros' ? 'sens' : 'warn'}>{i.blockedOn}</Chip>
          <span className="mt-1 block text-[10.5px] text-text-3">
            {i.attempts === 0
              ? 'Sin intentos'
              : `${String(i.attempts)} de ${String(data?.maxAttempts ?? 5)} intentos`}
            {i.exhausted ? ' · ya no reintenta solo' : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (i) => (
        // `stamping` has no retry, and that is not an oversight: the invoice may
        // have a CFDI at the SAT that we have not recorded, and only the
        // reconcile may release it — after asking Facturama.
        <span className="flex justify-end">
          {i.status === 'stamping' ? (
            <span className="text-[11px] text-text-3">En curso</span>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={retry.isPending && retry.variables === i.invoiceId}
              onClick={() => retry.mutate(i.invoiceId)}
            >
              {retry.isPending && retry.variables === i.invoiceId ? 'Encolando…' : 'Reintentar'}
            </Button>
          )}
        </span>
      ),
    },
  ]

  return (
    <div data-testid="admin-cfdi-queue" className="mx-auto w-full max-w-6xl py-6">
      <PageHeader
        title="Facturación fiscal"
        subtitle="Facturas pagadas que todavía no tienen CFDI, en todos los clientes."
      />

      <div className="mt-4">
        {isError ? (
          <QueryError
            message="No pudimos cargar la cola de facturación fiscal."
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          <Card>
            <Card.Body>
              <Spinner label="Cargando…" />
            </Card.Body>
          </Card>
        ) : items.length === 0 ? (
          <EmptyStateCard
            icon={<DocIcon />}
            title="Todo timbrado"
            body="Ninguna factura pagada está esperando su CFDI. Cuando una se atore, aparece aquí con el motivo y a quién le toca resolverlo."
          />
        ) : (
          <Card>
            <Card.Head
              title={
                (data?.total ?? items.length) > items.length
                  ? `${String(items.length)} de ${String(data?.total ?? 0)} sin CFDI`
                  : `${String(items.length)} sin CFDI`
              }
              sub={
                ours > 0
                  ? `${String(ours)} dependen de nosotros y ${String(theirs)} del cliente. La más antigua va primero.`
                  : `Las ${String(theirs)} esperan datos fiscales del cliente. La más antigua va primero.`
              }
            />
            <Card.Body>
              <DataTable
                caption="Facturas pagadas sin CFDI, por cliente"
                columns={columns}
                rows={items}
              />
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  )
}
